import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import { SideTooltip } from '@/components/ui/tooltip';
import { CHAT_UNREAD_BADGE_MAXIMUM } from '@/constants/chat';
import { cn } from '@/lib/utils';
import type { PractitionerNavigationItem } from './navigationItems';

interface PractitionerSidebarItemProps {
  item: PractitionerNavigationItem;
  isCollapsed: boolean;
  unreadCount: number;
  unreadBadgeAriaLabel: string;
}

function buildUnreadBadgeLabel(unreadCount: number): string {
  return unreadCount > CHAT_UNREAD_BADGE_MAXIMUM
    ? String(CHAT_UNREAD_BADGE_MAXIMUM) + '+'
    : String(unreadCount);
}

export function PractitionerSidebarItem({
  item,
  isCollapsed,
  unreadCount,
  unreadBadgeAriaLabel,
}: PractitionerSidebarItemProps) {
  const { t } = useTranslation(['onboarding', 'common', 'navigation']);
  const label = t(item.labelKey);
  const hasUnreadBadge = item.unreadBadge === true && unreadCount > 0;

  if (item.route === null) {
    return (
      <SideTooltip
        isEnabled={isCollapsed}
        label={t('navigation:sidebar.comingSoonItem', { label })}
      >
        <div
          aria-label={isCollapsed ? t('navigation:sidebar.comingSoonItem', { label }) : undefined}
          className={cn(
            'flex items-center gap-3 py-2.5 rounded-xl font-medium text-zoomuted opacity-60 grayscale',
            isCollapsed ? 'justify-center px-0' : 'px-3'
          )}
        >
          <Icon name={item.iconName} className="text-zooviolet" />
          {isCollapsed ? null : (
            <>
              <span className="flex-1 truncate">{label}</span>
              <Badge tone="muted" shape="tag" uppercase className="shrink-0 whitespace-nowrap">
                {t('common:state.comingSoon')}
              </Badge>
            </>
          )}
        </div>
      </SideTooltip>
    );
  }

  return (
    <SideTooltip
      isEnabled={isCollapsed}
      label={hasUnreadBadge ? label + ' · ' + buildUnreadBadgeLabel(unreadCount) : label}
    >
      <NavLink
        to={item.route}
        end
        aria-label={isCollapsed ? label : undefined}
        className={function resolveClassName({ isActive }) {
          return cn(
            'relative flex items-center gap-3 py-2.5 rounded-xl font-medium transition-colors',
            isCollapsed ? 'justify-center px-0' : 'px-3',
            isActive === true
              ? 'bg-zooprimary text-zoowhite shadow-sm'
              : 'text-zoomuted hover:bg-zoopalelavender hover:bg-opacity-30 hover:text-zooindigo'
          );
        }}
      >
        {function renderLinkContent({ isActive }) {
          return (
            <>
              <Icon
                name={item.iconName}
                className={isActive === true ? 'text-zoowhite' : 'text-zooviolet'}
              />
              {isCollapsed ? null : <span className="flex-1 truncate">{label}</span>}
              {hasUnreadBadge ? (
                <span
                  aria-label={unreadBadgeAriaLabel}
                  className={cn(
                    'flex items-center justify-center rounded-full font-bold shrink-0',
                    isCollapsed
                      ? 'absolute top-1 right-1.5 min-w-[16px] h-[16px] px-0.5 text-[9px]'
                      : 'min-w-[18px] h-[18px] px-1 text-[10px]',
                    isActive === true
                      ? 'bg-zoowhite text-zooprimary'
                      : 'bg-zooprimary text-zoowhite'
                  )}
                >
                  {buildUnreadBadgeLabel(unreadCount)}
                </span>
              ) : null}
            </>
          );
        }}
      </NavLink>
    </SideTooltip>
  );
}
